import { useToken } from '@ori/auth';
import { useServiceUrls } from '@ori/configuration-manager';
import { ApiClientProvider as GlobalApiClientProvider } from '@ori/global-api-client-react';
import { TranslationsNamespaceProvider } from '@ori/i18n';
import { ApiClientProvider as StaticApiClientProvider } from '@ori/static-api-client-react';
import { Team, TestIdProvider } from '@ori/testid-generator';
import { type FC, Suspense } from 'react';

import { APP_NAME, translationsNamespace } from './constants';
import { ErrorBoundary } from './containers/ErrorBoundary';
import { FooterAppSkeleton } from './containers/FooterAppSkeleton';
import { FooterContent } from './containers/FooterContent';
import { AppConfigProvider } from './contexts';
import type { AppConfig } from './models';

export interface AppProps {
  configuration: AppConfig;
}

export const App: FC<AppProps> = ({ configuration }) => {
  const { currentLanguage, tenant } = configuration;
  const { staticApiUrl, globalApiUrl } = useServiceUrls(['staticApi', 'globalApi']);
  const { formattedToken } = useToken();

  return (
    <TranslationsNamespaceProvider namespace={translationsNamespace}>
      <ErrorBoundary>
        <AppConfigProvider value={configuration}>
          <Suspense fallback={<FooterAppSkeleton />}>
            <TestIdProvider
              team={Team.Presentation}
              project={APP_NAME}
            >
              <StaticApiClientProvider
                baseUrl={staticApiUrl}
                tenant={tenant}
                language={currentLanguage}
              >
                <GlobalApiClientProvider
                  baseUrl={globalApiUrl}
                  tenant={tenant}
                  language={currentLanguage}
                  token={formattedToken}
                >
                  <FooterContent />
                </GlobalApiClientProvider>
              </StaticApiClientProvider>
            </TestIdProvider>
          </Suspense>
        </AppConfigProvider>
      </ErrorBoundary>
    </TranslationsNamespaceProvider>
  );
};
